import React, { useState, useEffect } from 'react';
import { Link2, Shield, AlertTriangle, CheckCircle2, XCircle, ArrowRight, Info, ShieldAlert, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, GlassCard } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { runLinkAnalysis } from '@/lib/mlClient';
import { MLLinkInsights, PlainSummary, MLReasoning } from './MLInsights';
import { explainLink } from '@/lib/plainExplanation';
import { useApp } from '@/context/AppContext';
import { cn } from '@/lib/utils';

// Safety Score Ring Component - Responsive sizing
const SafetyScoreRing = ({ score, size = 140, mobileSize = 120, strokeWidth = 12 }) => {
  const radius = (size - strokeWidth) / 2;
  const mobileRadius = (mobileSize - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const mobileCircumference = mobileRadius * 2 * Math.PI;
  const offset = circumference - (score / 100) * circumference;
  const mobileOffset = mobileCircumference - (score / 100) * mobileCircumference;
  
  const getColor = () => {
    if (score >= 80) return "hsl(var(--success))";
    if (score >= 50) return "hsl(var(--warning))";
    return "hsl(var(--destructive))";
  };

  const getLabel = () => {
    if (score >= 80) return "Safe";
    if (score >= 50) return "Caution";
    return "Dangerous";
  };

  return (
    <div className="relative inline-flex items-center justify-center">
      {/* Mobile Ring */}
      <svg width={mobileSize} height={mobileSize} className="transform -rotate-90 sm:hidden">
        <circle
          cx={mobileSize / 2}
          cy={mobileSize / 2}
          r={mobileRadius}
          fill="none"
          stroke="hsl(var(--muted))"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={mobileSize / 2}
          cy={mobileSize / 2}
          r={mobileRadius}
          fill="none"
          stroke={getColor()}
          strokeWidth={strokeWidth}
          strokeDasharray={mobileCircumference}
          strokeDashoffset={mobileOffset}
          strokeLinecap="round"
          className="transition-all duration-700 ease-out"
        />
      </svg>
      
      {/* Desktop Ring */}
      <svg width={size} height={size} className="transform -rotate-90 hidden sm:block">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="hsl(var(--muted))"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={getColor()}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-700 ease-out"
        />
      </svg>
      
      <div className="absolute flex flex-col items-center justify-center">
        <span className="text-2xl sm:text-3xl font-bold font-display" style={{ color: getColor() }}>
          {score}
        </span>
        <span className="text-xs text-muted-foreground font-medium">{getLabel()}</span>
      </div>
    </div>
  );
};

// Safety Progress Bar Component
const SafetyBar = ({ score }) => {
  const getBarColor = () => {
    if (score >= 80) return 'bg-success';
    if (score >= 50) return 'bg-warning';
    return 'bg-destructive';
  };

  return (
    <div className="relative h-3 sm:h-4 w-full overflow-hidden rounded-full bg-muted">
      <div
        className={cn("h-full transition-all duration-500 ease-out rounded-full", getBarColor())}
        style={{ width: `${score}%` }}
      />
    </div>
  );
};

// Mobile-first Safety Score Display
const SafetyScoreDisplay = ({ result }) => {
  const getVerdictConfig = () => {
    switch (result.verdict) {
      case 'Safe':
        return { icon: ShieldCheck, color: 'text-success', bg: 'bg-success/10', border: 'border-success/30', badge: 'safe' };
      case 'Caution':
        return { icon: AlertTriangle, color: 'text-warning', bg: 'bg-warning/10', border: 'border-warning/30', badge: 'suspicious' };
      case 'Dangerous':
        return { icon: ShieldAlert, color: 'text-destructive', bg: 'bg-destructive/10', border: 'border-destructive/30', badge: 'danger' };
      default:
        return { icon: Info, color: 'text-muted-foreground', bg: 'bg-muted', border: 'border-border', badge: 'unknown' };
    }
  };

  const config = getVerdictConfig();
  const Icon = config.icon;

  return (
    <GlassCard className={cn("p-4 sm:p-6", config.border, "border-2")}>
      {/* Mobile: Stacked, Desktop: Side by side */}
      <div className="flex flex-col items-center text-center lg:flex-row lg:items-start lg:text-left gap-4 lg:gap-6">
        {/* Score Ring - Centered on mobile */}
        <div className="flex-shrink-0">
          <SafetyScoreRing score={result.safetyScore} />
        </div>
        
        {/* Info */}
        <div className="flex-1 space-y-3">
          <div className="flex flex-wrap items-center gap-2 justify-center lg:justify-start">
            <Badge variant={config.badge} className="text-sm">
              <Icon className="w-4 h-4 mr-1" />
              {result.verdict}
            </Badge>
            {result.hardRedFlags && result.hardRedFlags.length > 0 && (
              <Badge variant="danger" className="text-xs animate-pulse">
                {result.hardRedFlags.length} Critical
              </Badge>
            )}
          </div>
          
          <div>
            <h3 className="font-display text-lg sm:text-xl font-bold text-foreground">
              Link Safety Score
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {result.safetyScore >= 80 
                ? "This link appears safe based on our analysis."
                : result.safetyScore >= 50
                ? "Exercise caution - some concerns detected."
                : "Multiple threats detected - avoid this link."}
            </p>
          </div>

          {/* Safety Bar */}
          <div>
            <div className="flex justify-between items-center mb-1.5">
              <span className="text-xs text-muted-foreground">Safety Level</span>
              <span className="text-xs font-mono">
                <span className={cn("font-bold", config.color)}>{result.safetyScore}</span>
                <span className="text-muted-foreground">/100</span>
              </span>
            </div>
            <SafetyBar score={result.safetyScore} />
            <div className="flex justify-between text-[10px] sm:text-xs text-muted-foreground mt-1">
              <span>Dangerous</span>
              <span>Caution</span>
              <span>Safe</span>
            </div>
          </div>
        </div>
      </div>
    </GlassCard>
  );
};

// Risk Summary Component
const RiskSummary = ({ result }) => {
  const config = result.verdict === 'Safe' 
    ? { border: 'border-success/30' }
    : result.verdict === 'Caution'
    ? { border: 'border-warning/30' }
    : { border: 'border-destructive/30' };

  return (
    <div className="space-y-4">
      {/* URL Details */}
      <GlassCard className="p-4 sm:p-5">
        <h4 className="text-sm font-semibold mb-3">URL Details</h4>
        <div className="space-y-2">
          <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-muted/50">
            <span className="text-xs sm:text-sm text-muted-foreground">Domain</span>
            <span className="text-xs sm:text-sm font-mono font-medium truncate max-w-[150px] sm:max-w-[200px]">
              {result.details.domain}
            </span>
          </div>
          <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-muted/50">
            <span className="text-xs sm:text-sm text-muted-foreground">TLD</span>
            <span className="text-xs sm:text-sm font-mono font-medium">{result.details.tld || 'N/A'}</span>
          </div>
          <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-muted/50">
            <span className="text-xs sm:text-sm text-muted-foreground">HTTPS</span>
            <Badge variant={result.details.hasHttps ? 'safe' : 'danger'} className="text-xs">
              {result.details.hasHttps ? 'Secure' : 'Not Secure'}
            </Badge>
          </div>
          {result.details.isTrustedBrand && (
            <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-success/10">
              <span className="text-xs sm:text-sm text-success">Verified Brand</span>
              <CheckCircle2 className="w-4 h-4 text-success" />
            </div>
          )}
        </div>
      </GlassCard>

      {/* Hard Red Flags */}
      {result.hardRedFlags && result.hardRedFlags.length > 0 && (
        <div className="p-4 rounded-xl bg-destructive/10 border-2 border-destructive/30">
          <div className="flex items-center gap-2 mb-2">
            <ShieldAlert className="w-5 h-5 text-destructive flex-shrink-0" />
            <span className="font-semibold text-sm text-destructive">Critical Threats</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {result.hardRedFlags.map((flag, i) => (
              <Badge key={i} variant="danger" className="text-xs">
                {flag.replace(/_/g, ' ')}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Top Risk Factors */}
      {result.topReasons && result.topReasons.length > 0 && (
        <GlassCard className="p-4 sm:p-5">
          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-warning" />
            Top Risk Factors
          </h4>
          <div className="space-y-2">
            {result.topReasons.map((flag, index) => (
              <div 
                key={index}
                className={cn(
                  "p-3 rounded-lg border",
                  flag.isHardRedFlag ? 'bg-destructive/10 border-destructive/30' :
                  flag.severity === 'critical' ? 'bg-destructive/10 border-destructive/30' :
                  flag.severity === 'high' ? 'bg-warning/10 border-warning/30' :
                  'bg-muted/50 border-border'
                )}
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-5 h-5 rounded-full bg-background flex items-center justify-center text-xs font-bold flex-shrink-0">
                      {index + 1}
                    </span>
                    <span className="font-medium text-xs sm:text-sm truncate">{flag.title}</span>
                  </div>
                  <span className="text-xs font-mono text-destructive flex-shrink-0">{flag.penalty}</span>
                </div>
                <p className="text-xs text-muted-foreground ml-7">{flag.description}</p>
              </div>
            ))}
          </div>
        </GlassCard>
      )}

      {/* Positive Signals */}
      {result.flags && result.flags.filter(f => f.type === 'positive').length > 0 && (
        <GlassCard className="p-4 sm:p-5">
          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-success" />
            Positive Signals
          </h4>
          <div className="space-y-2">
            {result.flags.filter(f => f.type === 'positive').map((flag, index) => (
              <div key={index} className="p-3 rounded-lg border bg-success/10 border-success/30">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-xs sm:text-sm text-success">{flag.title}</span>
                  <span className="text-xs font-mono text-success">+{flag.bonus}</span>
                </div>
                <p className="text-xs text-muted-foreground">{flag.description}</p>
              </div>
            ))}
          </div>
        </GlassCard>
      )}

      {/* Recommendations */}
      <GlassCard className="p-4 sm:p-5">
        <h4 className="text-sm font-semibold mb-3">Recommendations</h4>
        <ul className="space-y-2">
          {result.recommendations.map((rec, index) => (
            <li key={index} className="flex items-start gap-2 text-xs sm:text-sm text-muted-foreground">
              <ArrowRight className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
              <span>{rec}</span>
            </li>
          ))}
        </ul>
      </GlassCard>
    </div>
  );
};

export const LinkVerification = ({ sharedUrl, onSharedUrlConsumed }) => {
  const [url, setUrl] = useState('');
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { addVerification } = useApp();

  // Handle shared URL from query params
  useEffect(() => {
    if (sharedUrl) {
      setUrl(sharedUrl);
      runVerification(sharedUrl);
      onSharedUrlConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedUrl]);

  const runVerification = async (targetUrl) => {
    if (!targetUrl.trim()) return;

    setIsLoading(true);
    await new Promise(resolve => setTimeout(resolve, 800));

    const verificationResult = await runLinkAnalysis(targetUrl);
    setResult(verificationResult);

    addVerification({
      type: 'link',
      input: targetUrl,
      verdict: verificationResult.verdict,
      safetyScore: verificationResult.safetyScore,
      flagCount: verificationResult.flags.filter(f => f.type !== 'positive').length,
      hardRedFlags: verificationResult.hardRedFlags?.length || 0,
    });

    setIsLoading(false);
  };

  const handleVerify = () => runVerification(url);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleVerify();
  };

  const exampleLinks = [
    { url: 'https://google.com', label: 'Safe site' },
    { url: 'http://g00gle-login.xyz/verify', label: 'Typosquatting' },
    { url: 'http://secure-paypal.fake-site.tk/account', label: 'Domain mismatch' },
  ];

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header - Mobile optimized */}
      <div>
        <div className="flex items-center gap-2 sm:gap-3 mb-2">
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl gradient-primary flex items-center justify-center">
            <Link2 className="w-4 h-4 sm:w-5 sm:h-5 text-primary-foreground" />
          </div>
          <h2 className="font-display text-xl sm:text-2xl font-bold text-foreground">Link Verification</h2>
        </div>
        <p className="text-sm sm:text-base text-muted-foreground">
          Check any URL for phishing and security threats. Get a Safety Score from 0-100.
        </p>
      </div>

      {/* Desktop Two-Column Layout for Input + Results */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column: Input */}
        <div className="space-y-6">
          <GlassCard className="p-4 sm:p-6">
            <div className="space-y-4">
              {/* Input Field - Full width, stacked on mobile */}
              <div className="space-y-3">
                <div className="relative">
                  <Input
                    type="url"
                    placeholder="Paste URL to verify..."
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="h-12 sm:h-14 pl-10 sm:pl-12 text-base w-full"
                    data-testid="link-input"
                  />
                  <Link2 className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
                </div>
                
                {/* Button - Full width on mobile, min-h 48px */}
                <Button 
                  onClick={handleVerify} 
                  disabled={!url.trim() || isLoading}
                  className="w-full h-12 sm:h-14 text-base font-medium"
                  variant="gradient"
                  data-testid="verify-button"
                >
                  {isLoading ? (
                    <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                  ) : (
                    <>
                      <Shield className="w-5 h-5 mr-2" />
                      Check Safety
                    </>
                  )}
                </Button>
              </div>

              {/* Example Links */}
              <div>
                <p className="text-xs text-muted-foreground mb-2">Try examples:</p>
                <div className="flex flex-wrap gap-2">
                  {exampleLinks.map((example, index) => (
                    <button
                      key={index}
                      onClick={() => setUrl(example.url)}
                      className={cn(
                        "px-3 py-2 rounded-lg text-xs font-medium transition-colors min-h-[36px]",
                        example.label === 'Safe site' 
                          ? "bg-success/10 hover:bg-success/20 text-success"
                          : "bg-destructive/10 hover:bg-destructive/20 text-destructive"
                      )}
                    >
                      {example.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </GlassCard>

          {/* Desktop: Show Safety Score in left column when result exists */}
          {result && (
            <div className="hidden lg:block">
              <SafetyScoreDisplay result={result} />
            </div>
          )}

          {/* Info Cards - Only show when no result */}
          {!result && (
            <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 gap-4">
              <Card className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-2 sm:pb-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-destructive/10 flex items-center justify-center mb-2">
                    <ShieldAlert className="w-4 h-4 sm:w-5 sm:h-5 text-destructive" />
                  </div>
                  <CardTitle className="text-sm sm:text-base">Typosquatting Detection</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-xs sm:text-sm">
                    Catches domains like g00gle.com using character tricks.
                  </CardDescription>
                </CardContent>
              </Card>

              <Card className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-2 sm:pb-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-warning/10 flex items-center justify-center mb-2">
                    <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-warning" />
                  </div>
                  <CardTitle className="text-sm sm:text-base">Domain Analysis</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-xs sm:text-sm">
                    Identifies brand impersonation and suspicious TLDs.
                  </CardDescription>
                </CardContent>
              </Card>

              <Card className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-2 sm:pb-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-success/10 flex items-center justify-center mb-2">
                    <ShieldCheck className="w-4 h-4 sm:w-5 sm:h-5 text-success" />
                  </div>
                  <CardTitle className="text-sm sm:text-base">Safety Scoring</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-xs sm:text-sm">
                    Get a 0-100 score with Hard Red Flag detection.
                  </CardDescription>
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {/* Right Column: Results (Mobile: shows below input, Desktop: side by side) */}
        {result && (
          <div className="space-y-6">
            {/* Mobile: Show Safety Score here */}
            <div className="lg:hidden">
              <SafetyScoreDisplay result={result} />
            </div>

            {/* Plain-English summary for everyday users */}
            <PlainSummary summary={explainLink(result)} testId="link-plain-summary" />

            {/* Risk Summary */}
            <RiskSummary result={result} />

            {/* ML Signal Breakdown */}
            <MLReasoning testId="link-ml-toggle">
              <MLLinkInsights ml={result.ml} />
            </MLReasoning>
          </div>
        )}
      </div>
    </div>
  );
};

export default LinkVerification;
